import React, { useState, useEffect } from "react";
import "../Css/Research.css";
import "../Css/ParkinsonSession.css";
import PingPongParkinsonLogo from "../Assets/PingPongParkinson-Logo.jpeg";

// ── Sheet CSV export URLs ─────────────────────────────────────────────────────

const SEATTLE_SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/14vJxfT8u9udM_EEhYdhmxdROzt5E491WTEWiU8U4B_w/export?format=csv&gid=0";

const CALIFORNIA_SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/1U65slOgDw54FnUzs-9McEFGXoU4C56jXjswoG-1au4Q/export?format=csv&gid=0";

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function parseDate(raw) {
  if (!raw || raw.trim() === "") return null;
  const s = raw.trim();
  const currentYear = new Date().getFullYear();

  // ISO: 2026-04-25
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }

  // Slash: 4/25/2026 or 4/25
  const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/);
  if (slashMatch) {
    const month = parseInt(slashMatch[1], 10) - 1;
    const day = parseInt(slashMatch[2], 10);
    const year = slashMatch[3] ? parseInt(slashMatch[3], 10) : currentYear;
    const d = new Date(year, month, day);
    return isNaN(d) ? null : d;
  }

  // "April 25" or "Apr 25" or "April 25, 2026"
  const monthDayMatch = s.match(/^([A-Za-z]+)\s+(\d{1,2})(?:,\s*(\d{4}))?$/);
  if (monthDayMatch) {
    const year = monthDayMatch[3]
      ? parseInt(monthDayMatch[3], 10)
      : currentYear;
    const d = new Date(`${monthDayMatch[1]} ${monthDayMatch[2]}, ${year}`);
    return isNaN(d) ? null : d;
  }

  // Last resort
  const fallback = new Date(s);
  return isNaN(fallback) ? null : fallback;
}

function formatDate(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Scans every cell in every row for a parseable future date.
 * Accepts a sheetCsvUrl so it can be reused for any club's sheet.
 */
async function fetchUpcomingSessions(sheetCsvUrl) {
  const response = await fetch(sheetCsvUrl);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const text = await response.text();

  const lines = text
    .split("\n")
    .map((l) => l.replace(/\r$/, ""))
    .filter((l) => l.trim() !== "");

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sessions = [];

  for (let r = 0; r < lines.length; r++) {
    const cols = parseCSVLine(lines[r]);
    for (const cell of cols) {
      const date = parseDate(cell);
      if (date && date >= today) {
        if (!sessions.find((s) => s.date.getTime() === date.getTime())) {
          sessions.push({ rawDate: cell.trim(), date, location: "", time: "" });
        }
      }
    }
  }

  sessions.sort((a, b) => a.date - b.date);
  return sessions;
}

// ── Static data ──────────────────────────────────────────────────────────────

const benefits = [
  {
    icon: "🏓",
    title: "Motor Control",
    desc: "Repetitive paddle movements help improve hand-eye coordination and fine motor skills.",
  },
  {
    icon: "⚖️",
    title: "Balance & Stability",
    desc: "Dynamic footwork and body positioning during play strengthen balance and reduce fall risk.",
  },
  {
    icon: "🧠",
    title: "Cognitive Engagement",
    desc: "Fast-paced rallies challenge reaction time, focus, and strategic thinking.",
  },
  {
    icon: "❤️",
    title: "Emotional Well-being",
    desc: "Community sessions foster social connection, reduce isolation, and boost mood.",
  },
];

// ── Sub-component: one club's session box ────────────────────────────────────

function ClubSessionBox({ clubName, address, phone, schedule, sessions, loading, error, note }) {
  const nextSession = sessions[0] || null;
  const futureSessions = sessions.slice(1);

  return (
    <div className="parkinson-club-box">
      <div className="parkinson-club-box-header">
        <span className="parkinson-club-icon">📍</span>
        <h3 className="parkinson-club-name">{clubName}</h3>
      </div>

      <div className="parkinson-club-address">{address}</div>
      {schedule && (
        <div className="parkinson-club-schedule">🕐 {schedule}</div>
      )}
      {phone && (
        <div className="parkinson-club-phone">
          📞 <a href={`tel:${phone.replace(/\D/g, "")}`} className="parkinson-link">{phone}</a>
        </div>
      )}


      <div className="parkinson-club-divider" />

      {loading && (
        <div className="parkinson-sessions-loading">
          <span className="parkinson-sessions-spinner" />
          Loading upcoming dates…
        </div>
      )}

      {!loading && error && (
        <p className="parkinson-sessions-error">
          Unable to load session dates right now. Please{" "}
          <a href="mailto:info@sportsforequity.live" className="parkinson-link">
            contact us
          </a>
          .
        </p>
      )}

      {!loading && !error && !nextSession && (
        <p className="parkinson-sessions-none">
          No upcoming sessions scheduled yet. Stay tuned!
        </p>
      )}

      {!loading && !error && nextSession && (
        <>
          <div className="parkinson-next-session-date">
            {formatDate(nextSession.date)}
          </div>

          {note && (
            <div className="parkinson-session-note">
              <p className="parkinson-session-note-text">{note.line1}</p>
              <a href={`mailto:${note.email}`} className="parkinson-session-note-email">
                {note.email}
              </a>
            </div>
          )}

          {futureSessions.length > 0 && (
            <div className="parkinson-future-sessions">
              <p className="parkinson-future-label">Also coming up:</p>
              <ul className="parkinson-future-list">
                {futureSessions.map((s, i) => (
                  <li key={i} className="parkinson-future-item">
                    <span className="parkinson-future-date">
                      {formatDate(s.date)}
                    </span>
                    {s.time && (
                      <span className="parkinson-future-meta">
                        &nbsp;·&nbsp;🕐 {s.time}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

const ParkinsonSession = () => {
  const [seattleSessions, setSeattleSessions]     = useState([]);
  const [seattleLoading, setSeattleLoading]       = useState(true);
  const [seattleError, setSeattleError]           = useState(false);

  const [calSessions, setCalSessions]             = useState([]);
  const [calLoading, setCalLoading]               = useState(true);
  const [calError, setCalError]                   = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "PingPongParkinson | SportsForEquity";

    let metaDescription = document.querySelector('meta[name="description"]');
    let createdMeta = false;
    const prevDescription = metaDescription ? metaDescription.getAttribute("content") : null;

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
      createdMeta = true;
    }
    metaDescription.setAttribute(
      "content",
      "SportsForEquity partners with PingPongParkinson to host table tennis sessions designed for the Parkinson's community, supporting movement, coordination, and overall well-being."
    );

    fetchUpcomingSessions(SEATTLE_SHEET_CSV_URL)
      .then((data) => { setSeattleSessions(data); setSeattleLoading(false); })
      .catch(() => { setSeattleError(true); setSeattleLoading(false); });

    fetchUpcomingSessions(CALIFORNIA_SHEET_CSV_URL)
      .then((data) => { setCalSessions(data); setCalLoading(false); })
      .catch(() => { setCalError(true); setCalLoading(false); });

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        metaDescription.remove();
      } else if (prevDescription !== null) {
        metaDescription.setAttribute("content", prevDescription);
      }
    };
  }, []);

  return (
    <div className="parkinson-page">
      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="parkinson-hero">
        <div className="parkinson-hero-text">
          <div className="parkinson-hero-title-row">
            <a
              href="https://www.pingpongparkinson.org/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Ping Pong Parkinson website"
            >
              <img
                src={PingPongParkinsonLogo}
                alt="Ping Pong Parkinson Logo"
                className="parkinson-hero-logo"
              />
            </a>
            <h1 className="parkinson-title">PingPongParkinson</h1>
          </div>
          <p className="parkinson-tagline">
            PingPongParkinson (PPP) is a global sports organization dedicated to empowering individuals with Parkinson's disease through table tennis. By combining table tennis as a brain sport with non-pharmaceutical therapy, PPP creates an inclusive and supportive community where participants can enhance movement, coordination, and developing neuroplasticity. Through weekly sessions, players engage in table tennis as a fun and effective form of physical therapy, helping to improve motor skills and overall well-being. Whether playing recreationally or training for international competition—including the World Championships—PPP offers opportunities for all skill levels.
          </p>
        </div>
      </section>

      {/* ── Coming Sessions ────────────────────────────────── */}
      <section className="parkinson-sessions-banner">
        <div className="parkinson-sessions-banner-header">
          <span className="parkinson-sessions-banner-icon">📅</span>
          <h2 className="parkinson-sessions-banner-title">Coming Sessions</h2>
        </div>

        <div className="parkinson-clubs-grid">
          <ClubSessionBox
            clubName="Seattle Pacific Table Tennis Club"
            address={"13249 NE 20th St,\nBellevue, WA 98005"}
            phone=""
            schedule="Saturdays 9:00 – 10:30 AM"
            sessions={seattleSessions}
            loading={seattleLoading}
            error={seattleError}
            note={{ line1: "Registration is not required.", email: "info@sportsforequity.live" }}
          />

          <ClubSessionBox
            clubName="California Table Tennis"
            address={"2727 Stingle Ave. #10,\nRosemead, CA 91770"}
            phone=""
            schedule="Sundays 5:30 PM – 7:00 PM"
            sessions={calSessions}
            loading={calLoading}
            error={calError}
          />

          <ClubSessionBox
            clubName="Golden State Ping Pong"
            address={"1364 Minnis Cir,\nMilpitas, CA 95035"}
            phone=""
            schedule="Saturdays 8:00 – 09:30 AM"
            sessions={[]}
            loading={false}
            error={false}
          />
        </div>
      </section>

      {/* ── About ──────────────────────────────────────────── */}
      <section className="parkinson-section">
        <h2 className="parkinson-section-heading">About Our Program</h2>
        <p className="parkinson-body">
          SportsForEquity partners with PingPongParkinson to host biweekly table tennis sessions designed specifically for the Parkinson's community. Our trained coaches lead structured, safe, and enjoyable play in a welcoming environment that supports movement, coordination, and overall well‑being.
        </p>
        <p className="parkinson-body">
          Please contact us at{" "}
          <a href="mailto:info@sportsforequity.live" className="parkinson-link">
            info@sportsforequity.live
          </a>
        </p>
      </section>

      {/* ── Benefits ───────────────────────────────────────── */}
      <section className="parkinson-section">
        <h2 className="parkinson-section-heading">How Table Tennis Helps</h2>
        <div className="parkinson-benefits-grid">
          {benefits.map((b) => (
            <div key={b.title} className="parkinson-benefit-card">
              <span className="parkinson-benefit-icon">{b.icon}</span>
              <h3 className="parkinson-benefit-title">{b.title}</h3>
              <p className="parkinson-benefit-desc">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ParkinsonSession;